import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { auth, onAuthStateChanged, getTenantId } from './FirebaseAuth'
import { getDocsFromAuthentication, getFeatureFlags } from '../firebase/cloudFunction/index'
import { getPermissions } from '../firebase/fireStore/roleManagementDBMethods'
import {
  TENANT_PERMISSION_TYPE,
  ADMIN_PERMISSION_TYPE,
  FIREBASE_USERNAME,
  TENANT_CREATED_TIME,
  LOGIN_TYPE,
  FETCH_USER_PERMISSION_FIREBASE,
} from '../redux/constants/Auth'
import { Spin } from 'antd'
import './Auth.css'
import checkIsFeatureEnabled from './Util'
import { PENDO_TRACKING } from 'constants/FeatureFlagConstant'
import { initializePendo, setPendoFeatureEnabled } from 'utils/pendoInit'

export const AuthContext = React.createContext()
export const SystemFlagContext = React.createContext()

const defaultFeatureFlagsContext = {
  flags: {
    system: [],
    tenant: {},
  },
  loaded: false,
}

export const FeatureFlagsContext = React.createContext(defaultFeatureFlagsContext)
export const useAuthContext = () => React.useContext(AuthContext)
export const useFeatureFlagsContext = () => React.useContext(FeatureFlagsContext)
export const FeaturesContext = React.createContext({})
export const useFeatures = () => React.useContext(FeaturesContext)

export const isFeatureEnabledInEnv = (featureName) => {
  if (!featureName) return false
  const envFeatureFlags = process.env.REACT_APP_FEATURE_FLAGS || ''
  const envFeatureList = envFeatureFlags.split(',').map((f) => f.trim())
  return envFeatureList.includes(featureName)
}

const isFeatureEnabled = (featureName, flags) => checkIsFeatureEnabled(featureName, flags) === 1

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveLoginType(claims) {
  if (claims.loginType) return claims.loginType
  return localStorage.getItem(LOGIN_TYPE) || '1'
}

async function applyTenantPermissions(user) {
  const { claims } = user
  if (!claims?.tenantId) return
  let tenantID = claims.tenantId
  let uuid = tenantID
  if (claims.role === ADMIN_PERMISSION_TYPE) {
    tenantID = claims.identity
  }
  const userPermissions = await getPermissions(tenantID, uuid)
  claims['userPermissions'] = userPermissions?.permissions?.length
    ? userPermissions
    : { permissions: [] }
}

async function applyAdminNativeLogin(user) {
  const { claims } = user
  const getUserPermissionFlag = localStorage.getItem(FETCH_USER_PERMISSION_FIREBASE)
  let doc
  if (getUserPermissionFlag === '1') {
    doc = await getDocsFromAuthentication(user.uid, '1')
  } else if (!getUserPermissionFlag) {
    localStorage.setItem(FETCH_USER_PERMISSION_FIREBASE, '1')
    doc = await getDocsFromAuthentication(user.uid, '')
  }
  claims[FIREBASE_USERNAME] = doc.data.userName
  claims['enrollmentTokenAvailable'] = doc.data.enrollmentTokenAvailable
  claims['user_id'] = doc.data.adminTenantId
  claims[TENANT_CREATED_TIME] = doc.data.createdDateTime
  claims['loginType'] = '1'
  claims['friendlyId'] = doc.data.friendlyCompanyId
  claims['userPermissions'] = { permissions: doc.data.permissions }
  claims['customRoleName'] = doc.data.customRoleName
}

function applyAdminSSOLogin(user) {
  user.claims['user_id'] = user.claims.identity
}

function applyEntraLogin(user) {
  const { claims } = user
  claims['user_id'] = claims.companyId
  claims['customRoleName'] = claims.zdnaRoles?.[0]?.name || null
  if (Array.isArray(claims.zdnaPermissions) && claims.zdnaPermissions.length) {
    claims['userPermissions'] = { permissions: claims.zdnaPermissions }
  }
}

// ── AuthProvider ──────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null)
  const [featureFlags, setFeatureFlags] = useState(null)
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false)
  const [pending, setPending] = useState(true)
  const [features, setFeatures] = useState({})

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        setPending(true)
        await setCustomClaims(user)
        await addFeatureFlags(user)
      } else {
        setPendoFeatureEnabled(false)
      }
      setCurrentUser(user)
      setPending(false)
    })
  }, [])

  useEffect(() => {
    if (featureFlagsLoaded && featureFlags && Array.isArray(featureFlags.system)) {
      const updatedFeaturesArray = featureFlags.system.map((featureConfig) => {
        const isEnabled = checkIsFeatureEnabled(featureConfig.featureName, featureFlags)
        console.log(`Feature: ${featureConfig.featureName}, Enabled: ${isEnabled}`)
        return { featureName: featureConfig.featureName, isEnabled }
      })
      const updatedFeatures = updatedFeaturesArray.reduce((acc, { featureName, isEnabled }) => {
        acc[featureName] = isEnabled
        return acc
      }, {})
      setFeatures(updatedFeatures)
    }
  }, [featureFlagsLoaded, featureFlags])

  async function setCustomClaims(user) {
    const idTokenResult = await user.getIdTokenResult()
    user.claims = idTokenResult.claims
    user.claims['role'] =
      user.claims.role === TENANT_PERMISSION_TYPE ? ADMIN_PERMISSION_TYPE : TENANT_PERMISSION_TYPE

    await applyTenantPermissions(user)

    const loginType = resolveLoginType(user.claims)
    console.log('LOGIN TYPE VALUS IS ', loginType)

    if (user.claims.role === ADMIN_PERMISSION_TYPE && loginType == '1') {
      await applyAdminNativeLogin(user)
    } else if (user.claims.role === ADMIN_PERMISSION_TYPE && loginType == '2') {
      applyAdminSSOLogin(user)
    } else if (loginType === 'entra') {
      applyEntraLogin(user)
    }
  }

  async function addFeatureFlags(user) {
    setFeatureFlagsLoaded(false)
    setPendoFeatureEnabled(false)
    let isPendoEnabled = false

    if (!user || !user.claims) {
      console.log('addFeatureFlags: No user or tenantId, skipping feature flags fetch.')
      setFeatureFlags(null)
      setPendoFeatureEnabled(false)
      setFeatureFlagsLoaded(true)
      return
    }

    try {
      const tenantId = getTenantId(user)
      console.log('addFeatureFlags: Fetching feature flags for tenant:', tenantId)
      const flags = await getFeatureFlags(tenantId)
      console.log('addFeatureFlags: Fetched flags:', flags)

      if (flags && typeof flags.system !== 'undefined' && typeof flags.tenant !== 'undefined') {
        setFeatureFlags(flags)
        isPendoEnabled = isFeatureEnabled(PENDO_TRACKING, flags)
        setPendoFeatureEnabled(isPendoEnabled)
        console.log('addFeatureFlags: Feature flags stored in context.')
      } else {
        console.warn(
          'addFeatureFlags: Fetched flags are not in the expected format, or getFeatureFlags resolved with an error-like structure. Flags:',
          flags
        )
        setFeatureFlags(null)
        setPendoFeatureEnabled(false)
      }
    } catch (error) {
      console.error(
        'addFeatureFlags: Error fetching or storing feature flags.',
        error?.message ? error.message : error,
        error?.errorDetail ? error.errorDetail : ''
      )
      setFeatureFlags(null)
      setPendoFeatureEnabled(false)
    } finally {
      if (isPendoEnabled) {
        await initializePendo(user)
      }
      setFeatureFlagsLoaded(true)
      console.log('addFeatureFlags: Feature flags loading completed.')
    }
  }

  if (pending) {
    return (
      <>
        <Spin size="large" tip="Loading" />
      </>
    )
  }

  const featureFlagsContextValue = {
    flags: featureFlags || defaultFeatureFlagsContext.flags,
    loaded: featureFlagsLoaded,
  }

  return (
    <AuthContext.Provider value={{ currentUser }}>
      <SystemFlagContext.Provider value={features.profile_dependency}>
        <FeatureFlagsContext.Provider value={featureFlagsContextValue}>
          <FeaturesContext.Provider value={features}>
            {children}
          </FeaturesContext.Provider>
        </FeatureFlagsContext.Provider>
      </SystemFlagContext.Provider>
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
